import { type DatabaseJSON } from '@databases/model/database';
import { type EntryValuesJSON } from '@databases/model/entry';
import { type FieldJSON } from '@databases/model/field';
import {
  type DatabaseViewJSON,
  type CollabLayoutType,
} from '@databases/model/views/view';
import { type ReactElement } from 'react';
import { type IntlShape } from 'react-intl-next';
import { v4 as uuidv4 } from 'uuid';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import {
  type TranslatableTemplateType,
  translationsMap,
  type TemplateType,
} from './types';

/**
 * Replaces field ids, entry ids and UUIDs with values corresponding
 * to the key "id".
 */
export const regenerateIdsExcludingRequiredUUIDValues = (json: DatabaseJSON) => {
  let databaseJson = JSON.stringify(json);
  // Uses positive look behind to match UUID values that correspond to the key "id".
  // e.g. "id": "66c363df-7338-4e7f-8cc7-350d85b31faf" will match 66c363df-7338-4e7f-8cc7-350d85b31faf
  // This method does not overwrite UUIDs that are required for entry values, such as the
  // fileId in the attachment field.
  const matchUuidFollowingIdKeyRegex = new RegExp(
    '(?<=["]id["][\\s]*:[\\s]*["])([a-z0-9]{8})-([a-z0-9]{4})-([a-z0-9]{4})-([a-z0-9]{4})-([a-z0-9]{12})',
    'g'
  );
  const existingUUIDs = new Set<string>([...json.fields, ...json.entries]);

  databaseJson = databaseJson.replace(matchUuidFollowingIdKeyRegex, (id) => {
    existingUUIDs.add(id);
    return id;
  });

  existingUUIDs.forEach((currentUUID) => {
    databaseJson = databaseJson.replaceAll(currentUUID, uuidv4());
  });

  return JSON.parse(databaseJson);
};

const regenerateIds = (json: DatabaseJSON) => {
  let databaseJson = JSON.stringify(json);
  const guidRegex = new RegExp(
    '(?<!["]value["][\\s]*:[\\s]*["])([a-z0-9]{8})-([a-z0-9]{4})-([a-z0-9]{4})-([a-z0-9]{4})-([a-z0-9]{12})',
    'g'
    // regex explanation:
    // (?<![stuff]) <-- negative look behind portion
    // (Nums & letrs)-(Nums & letrs)-(Nums & letrs)-(Nums & letrs) <-- uuid
    // ["]value["][\\s]*:[\\s]*["] <-- matches "value":"uuid"
    // but the negative look behind portion makes sure that it matches a uuid
    // that isn't preceded by a value key
    // for more info on look ahead and look behind see https://www.regular-expressions.info/lookaround.html
  );

  const existingUUIDs = new Set<string>();

  databaseJson.replace(guidRegex, (match) => {
    existingUUIDs.add(match);
    return match;
  });

  for (const uuid of existingUUIDs) {
    databaseJson = databaseJson.replaceAll(uuid, uuidv4());
  }

  return JSON.parse(databaseJson);
};

/**
 * Returns a date string in format yyyy-mm-dd
 * in the range: [current date, current date plus one year]
 */
const getRandomDateInNextYear = (): string => {
  const now = new Date();
  const oneYearLater = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
  const timeDiff = oneYearLater.getTime() - now.getTime();
  const randomTime = Math.random() * timeDiff;
  const randomDate = new Date(now.getTime() + randomTime);
  const newYear = randomDate.getFullYear();
  const newMonth = String(randomDate.getMonth() + 1).padStart(2, '0'); // add 1 because of zero based indexing
  const newDay = String(randomDate.getDate()).padStart(2, '0');
  // expect format yyyy-mm-dd
  return `${newYear}-${newMonth}-${newDay}`;
};

/**
 * Messages that contain HTML tags like <br> are formatted as an array of string elements and React elements.
 * This method replaces specifically the break tag (other elements do not exist in template messages) with '\n'
 * since valueJson.value must be assigned a string
 */
const parseMessage = (message: Array<string | ReactElement>): string => {
  let str = '';
  for (const element of message) {
    if (typeof element === 'string') {
      str += element;
    } else if (element['type'] === 'br') {
      str += '\n';
    }
  }
  return str;
};

export const processTemplate = ({
  templateType,
  databaseJson,
  intl,
  layoutType,
}: {
  templateType: TemplateType;
  databaseJson: DatabaseJSON;
  intl: IntlShape;
  layoutType?: CollabLayoutType;
}): DatabaseJSON => {
  const { formatMessage } = intl;
  const json: DatabaseJSON = JSON.parse(JSON.stringify(databaseJson)); // Make a deep copy
  const isNewSetupPanelEnabled = getExperimentValue('setupPanel', 'enabled', {
    fireExperimentExposure: false,
  });
  const messages = (templateType as TranslatableTemplateType)
    ? translationsMap[<TranslatableTemplateType>templateType]
    : undefined;

  const translateMessage = (message: string) => {
    if (!messages || !messages[message]) {
      return message;
    }
    const translatedMessage = formatMessage(messages[message]);
    if (Array.isArray(translatedMessage)) {
      return parseMessage(translatedMessage);
    }
    return translatedMessage;
  };

  const processEntryValues = (entryValues: EntryValuesJSON) => {
    for (const [, valueJson] of Object.entries(entryValues)) {
      if (valueJson.type === 'text' && valueJson.value) {
        const message = translateMessage(valueJson.value);
        valueJson.value = message;
      }

      if (isNewSetupPanelEnabled && valueJson.type === 'date') {
        valueJson.value = getRandomDateInNextYear();
      }
    }
  };

  const processFields = (fields: Record<string, FieldJSON>) => {
    for (const [, field] of Object.entries(fields)) {
      if ('name' in field) {
        field.name = translateMessage(field.name);
      }

      if ('selectOptions' in field && field.selectOptions.length) {
        for (const select of field.selectOptions) {
          select.label = translateMessage(select.label);
        }
      }
    }
  };

  const processViews = (views: Record<string, DatabaseViewJSON>) => {
    for (const [, view] of Object.entries(views)) {
      view.name = translateMessage(view.name);
    }
  };

  const setDefaultViewByLayoutType = (layoutType: CollabLayoutType) => {
    const matchingViewId = json.views.find(
      (viewId) => json.viewsById[viewId]?.layoutType === layoutType
    );

    if (matchingViewId) {
      // Set as default view
      json.defaultViewId = matchingViewId;
    }
  };

  for (const entry of Object.values(json.entriesById)) {
    processEntryValues(entry.values);
  }

  processFields(json.fieldsById);

  processViews(json.viewsById);

  // Set the view with matching layoutType as the default view if layoutType is provided
  if (layoutType !== undefined) {
    setDefaultViewByLayoutType(layoutType);
  }

  if (isNewSetupPanelEnabled) {
    return regenerateIdsExcludingRequiredUUIDValues(json);
  }

  return regenerateIds(json);
};
